import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { LabelWithTooltip } from '@hub/features/account-dashboard/components/label-with-tooltip';
import {
  DashboardCard,
  ProgressBar,
  Stack,
  Typography,
  useBreakpoints,
} from '@monorepo/nova-ui';
import { useDashboard } from '../../../../../contexts';

export interface PnlCardProps {
  pnlFromHwm: number;
  weeklyPerformancePercent: number;
  currentEquity: number;
  highWaterMark: number;
  initialBalance: number;
}

/**
 * P&L Card Component
 *
 * Displays profit and loss information with progress tracking.
 * Uses dashboard context for data access instead of props.
 */
export const PnlCard: FC = () => {
  // Get data from dashboard context instead of props
  const { statistics } = useDashboard();

  const { isTabletOrMobile } = useBreakpoints();

  const { t } = useTranslation('accountStatistics');

  // Use statistics from context with fallback defaults
  const {
    highWaterMark = 0,
    pnlFromHwm = 0,
    initialBalance = 0,
    currentEquity = 0,
  } = statistics || {};

  const formatPnlValue = (value: number) => {
    const sign = value >= 0 ? '' : '-';
    const absValue = Math.abs(value);
    return `${sign}$${absValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <DashboardCard
      sx={{
        flexDirection: 'column',
        gap: 2,
        justifyContent: 'space-between',
        maxHeight: isTabletOrMobile ? '250px' : '100%',
      }}
    >
      <Stack sx={{ width: '100%' }} gap={2}>
        <LabelWithTooltip
          label={t('cards.pnl.title')}
          tooltip={t('cards.pnl.tooltip')}
        />
        <Stack direction="row" gap={2} alignItems="center">
          <Typography
            sx={(theme) => ({
              fontSize: theme.typography.pxToRem(28),
              fontWeight: theme.typography.fontWeightMedium,
              color:
                pnlFromHwm >= 0
                  ? theme.palette.success.main
                  : theme.palette.error.main,
            })}
          >
            {formatPnlValue(pnlFromHwm)}
          </Typography>
        </Stack>
      </Stack>
      <Stack sx={{ width: '100%', height: '100%', justifyContent: 'flex-end' }}>
        {initialBalance > 0 && (
          <ProgressBar
            startLabel={t('cards.pnl.currentEquity')}
            startValue={initialBalance}
            endLabel={t('cards.pnl.hwm')}
            endValue={highWaterMark}
            currentValue={currentEquity}
          />
        )}
      </Stack>
    </DashboardCard>
  );
};
